import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import { useTranslations } from "use-intl";

import { recoilStoreModalState } from "~/atoms/changeStore";
import { useCartContext } from "~/contexts/cart";
import { useConfiguration } from "~/contexts/configuration";
import { useFavouriteStore } from "~/contexts/favourite-store";
import { Coords } from "~/shared/coords.class";
import Constants from "~/utils/constants";

import styles from "../checkout/checkout-shipping-step.module.scss";
import Icon from "../common/icon";
import StockDetailsModal from "./stock-details-modal";
import StoresModal, { calculateDistance, defaultLocation } from "./stores-modal";
import StoresSelection from "./stores-selection";

type Props = {
  open?: boolean;
  setOpen?: (status: boolean) => void;
  tempStore?: EVA.Core.ProductAvailabilityPickupOrganizationUnit;
  setStore: (store: EVA.Core.ProductAvailabilityPickupOrganizationUnit) => void;
  products: EVA.Core.GetProductAvailability.Product[];
  submitText?: string;
  error?: boolean;
  inModal?: boolean;
  storeId?: number | string;
  defaultFilerAvailable?: boolean;
  inPDP?: boolean;
};

export default function PickupStoreWrapper(props: Props) {
  const { eva } = useConfiguration();
  // TODO understand how to handle this
  // do we need to keep it syncronized with the checkout selected store
  // or we can have 2 different values?
  const { preferredStoreId } = useFavouriteStore();
  const { step, setStep } = useCartContext();
  const t = useTranslations();

  const [filterAvailableOnly, setFilterAvailableOnly] = useState(!!props.defaultFilerAvailable);
  const [locationPos, setLocationPos] = useState<Coords>(defaultLocation);
  const [showSelection, setShowSelection] = useState(props.inModal ? !preferredStoreId : !props.storeId);

  const storeState = useRecoilValue<boolean>(recoilStoreModalState);

  const query = useQuery({
    queryKey: ["getProductAvailabilityPickup", props.products, locationPos],
    keepPreviousData: true,
    staleTime: Constants.DEFAULT_STALE_TIME,
    queryFn: async () => await eva.getProductAvailability(props.products, true, false, locationPos),
  });

  const validPickupStores = useMemo(() => {
    const stores = query.data?.PickupOrganizationUnits ?? [];

    // make sure only stores with address and lat/lon are filtered
    const storesWithAddress = stores.filter(
      (store) => !!store.Address && store.IsPickup && (!!store.Address.Latitude || !!store.Latitude)
    );

    if (!!locationPos.lat && !!locationPos.lng) {
      const storeWithDistance = storesWithAddress.map((store) => {
        if (!store.DistanceInKm) {
          store.DistanceInKm = calculateDistance(
            locationPos.lat!,
            locationPos.lng!,
            (store.Latitude || store.Address.Latitude)!,
            (store.Longitude || store.Address.Longitude)!
          );
        }
        return store;
      });

      const storesSortedByDistance = storeWithDistance.sort((a, b) => {
        return a.DistanceInKm! - b.DistanceInKm!;
      });

      return storesSortedByDistance;
    }
    return storesWithAddress;
  }, [locationPos, query.data?.PickupOrganizationUnits]);

  // when inModal - set external store when preferredStoreId changes
  // when not inModal - init the store value
  const initStoreValue = useRef(false);
  useEffect(() => {
    if (validPickupStores && validPickupStores?.length > 0) {
      if (props.inModal) {
        const preselected = validPickupStores.find((store) => store.ID == preferredStoreId);
        if (preselected) {
          props.setStore(preselected);
        }
      } else if (!initStoreValue.current) {
        const selectedStore = validPickupStores.find((store) => store.ID == props.storeId);
        if (selectedStore) {
          props.setStore(selectedStore);
          initStoreValue.current = true;
        } else {
          setShowSelection(true);
        }
      }
    }
  }, [props.inModal, validPickupStores, props.storeId, props.setStore, props, preferredStoreId]);

  useEffect(() => {
    if (storeState) setShowSelection(true);
  }, [storeState]);

  useEffect(() => {
    if (step === "billing") setShowSelection(false);
  }, [step]);

  const pickupStoresFiltered = useMemo(() => {
    return validPickupStores.filter((value) => {
      if (filterAvailableOnly) {
        return value.HasStock === filterAvailableOnly;
      }
      return true;
    });
  }, [filterAvailableOnly, validPickupStores]);

  if (props.inModal && props.open != undefined) {
    return (
      <StoresModal
        open={props.open}
        onClose={() => {
          setLocationPos(defaultLocation);
          props.setOpen?.(false);
        }}
        locationPos={locationPos}
        setLocationPos={setLocationPos}
        stores={pickupStoresFiltered}
        filterAvailableOnly={filterAvailableOnly}
        setFilterAvailableOnly={setFilterAvailableOnly}
        isLoading={query.isLoading}
        inPDP={props.inPDP}
      />
    );
  }

  return showSelection ? (
    <div className={styles.pickupStoreWrapper}>
      <StoresSelection
        locationPos={locationPos}
        setLocationPos={setLocationPos}
        stores={pickupStoresFiltered}
        filterAvailableOnly={filterAvailableOnly}
        setFilterAvailableOnly={setFilterAvailableOnly}
        isLoading={query.isLoading}
        setStore={props.setStore}
        initialStoreId={props.storeId}
      />
    </div>
  ) : props.tempStore ? (
    <div
      className={classNames(
        styles.smallBox,
        { [styles.error]: props.error },
        { [styles.disabled]: !props.tempStore.HasStock },
        styles.selected
      )}
    >
      <p className={styles.pickupName}>
        <span>{props.tempStore.Name}</span>
        {preferredStoreId == props.tempStore.ID && (
          <Icon width={16} height={16} name="star-full" className={styles.preferredStore} />
        )}
      </p>
      <p className={styles.pickupAddress}>
        {props.tempStore?.Address.Address1}, {props.tempStore?.Address.City}, {props.tempStore?.Address.CountryID}
        {props.tempStore?.Address?.PhoneNumber ? (
          <>
            {` · `}
            <a href={`tel:${props.tempStore.Address.PhoneNumber}`}>{props.tempStore.Address.PhoneNumber}</a>
          </>
        ) : (
          ""
        )}
      </p>
      <div className={styles.pickupChangeRow}>
        <div className={styles.stock}>
          <div
            className={styles.stockIndicator}
            style={{ backgroundColor: props.tempStore?.HasStock ? "green" : "#CC0C0C" }}
          />
          <p className={styles.stockText}>
            {props.tempStore?.HasStock
              ? t("generic.productAvailableAtThisLocation")
              : t("generic.productUnavailableAtThisLocation")}
          </p>
        </div>
        <div className={styles.pickupChangeRow}>
          {!props.tempStore.HasStock && <StockDetailsModal store={props.tempStore} />}
          <button
            id="open-stores-modal"
            type="button"
            onClick={() => {
              setShowSelection(true);
              setStep("shipping");
            }}
          >
            {t("generic.changestore")}
          </button>
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
}
